import { Link } from 'react-router-dom';
import { MarketplaceArche } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArcheIconImg } from '@/lib/arche-icon-url';

interface ArcheCardProps {
  arche: MarketplaceArche;
}

export function ArcheCard({ arche }: ArcheCardProps) {
  const pricingLabel =
    arche.pricing.model === 'free'
      ? 'Free'
      : arche.pricing.model === 'fixed' && arche.pricing.priceCents
        ? `$${(arche.pricing.priceCents / 100).toFixed(2)}`
        : 'Variable';

  return (
    <Link to={`/marketplace/arches/${arche.slug || arche.archId}`} className="block h-full">
      <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
        <CardContent className="p-4 flex flex-col flex-1">
          {/* Square icon */}
          <div className="flex justify-center mb-3">
            {arche.imageRef ? (
              <ArcheIconImg
                imageRef={arche.imageRef}
                size={128}
                alt={arche.name}
                className="w-16 h-16 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-primary/80 to-primary flex items-center justify-center shadow-sm">
                <span className="text-2xl font-semibold text-primary-foreground">
                  {arche.name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Name */}
          <h3 className="text-sm font-semibold text-foreground text-center line-clamp-2 mb-1">
            {arche.name}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-3 mb-3 min-h-[3.75rem] text-center">
            {arche.simpleDescription}
          </p>

          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 mt-auto">
            {arche.categoryName && <Badge variant="outline">{arche.categoryName}</Badge>}
            <div className="flex items-center gap-3">
              <span title="Fias Fitness Score">FFS: {(arche.fitnessScore * 100).toFixed(0)}%</span>
              <span title="Times Used">{arche.invocationCount.toLocaleString()} runs</span>
            </div>
          </div>

          <div className="flex justify-end gap-1.5">
            {arche.hasPerUseCredits && (
              <Badge variant="outline" className="text-xs">
                Credits per use
              </Badge>
            )}
            <Badge variant={arche.pricing.model === 'free' ? 'secondary' : 'default'}>
              {pricingLabel}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
